import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import { MultipleSelectFilter } from "~components/MultipleSelectFilter/MultipleSelectFilter.tsx";
import SelectAgeGroup from "~components/SelectAgeGroup";
import SelectGender from "~components/SelectGender";
import SelectSeasonStandards from "~components/SelectSeasonStandards/SelectSeasonStandards.tsx";
import SelectSeasonStatus from "~components/SelectSeasonStatus";
import { TableFilter } from "~enums/tableFilter.ts";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope.ts";
import { SeasonTableFilterTypes } from "~pages/Seasons/SeasonListPage/enums/seasonTableFilterTypes.ts";
import type { Competition } from "~types/competition.ts";

export const useGetDashboardSeasonsDataTableFilters = (): {
  filters: DataTableFilter[];
  currentFilter: Record<string, any>;
  handleFilterChange: (updatedFilter: Record<string, any>) => void;
} => {
  const [currentFilter, setCurrentFilter] = useState<Record<string, any>>({});
  const { data: scopeData } = useGetCurrentUserPermissionScope();
  const { formatMessage } = useIntl();

  const competitionOptions = useMemo(
    () =>
      Object.values(scopeData?.resources?.competitions || {}).map(({ nameLocal, competitionId }: Competition) => ({
        value: competitionId,
        label: nameLocal,
      })),
    [scopeData],
  );

  const handleFilterChange = useCallback((updatedFilter: Record<string, any>) => {
    setCurrentFilter(updatedFilter);
  }, []);

  const filters = [
    {
      filterKey: SeasonTableFilterTypes.CompetitionName,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <MultipleSelectFilter
          options={competitionOptions}
          onChange={props.onChange}
          value={props.value}
          width={250}
          placeholder={formatMessage({
            id: "seasons.filters.competitions.allOption",
          })}
        />
      ),
    },
    {
      filterKey: SeasonTableFilterTypes.AgeGroup,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <SelectAgeGroup
          multiple
          onChange={props.onChange}
          value={props.value}
          placeholder={formatMessage({
            id: "dataTable.filters.ageGroup.allOption",
          })}
        />
      ),
    },
    {
      filterKey: SeasonTableFilterTypes.Gender,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <SelectGender
          multiple
          onChange={props.onChange}
          value={props.value}
          placeholder={formatMessage({
            id: "dataTable.filters.gender.allOption",
          })}
        />
      ),
    },
    {
      filterKey: SeasonTableFilterTypes.Standard,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <SelectSeasonStandards
          multiple
          onChange={props.onChange}
          value={props.value}
          placeholder={formatMessage({ id: "seasons.filters.standards.allOption" })}
        />
      ),
    },
    {
      filterKey: SeasonTableFilterTypes.Status,
      tableFilterType: TableFilter.Primary,
      filterElement: (props: { value: any; onChange: (value: any) => void }) => (
        <SelectSeasonStatus
          multiple
          onChange={props.onChange}
          value={props.value}
          placeholder={formatMessage({
            id: "seasons.filters.statuses.allOption",
          })}
        />
      ),
    },
  ];

  return {
    filters: filters,
    currentFilter: currentFilter,
    handleFilterChange: handleFilterChange,
  };
};
